import { useEffect } from 'react';
import type { ReroutingTransition } from './ReroutingTransitionValid.types.tsx';
import type { DiagramTransition, PortInfo } from './toPortInfo.tsx';
import {
	createTransitionFromProposed,
	updateTransitionAfterReroute,
} from './updateTransitionAfterReroute.tsx';
import type { DragDropState } from './useCanvasDragDropEvents.tsx';
import { useLatest } from './useLatest.tsx';
import type { ProposedTransition } from './useTransitionProposals.tsx';

export interface Options {
	ports: Record<string, PortInfo>;
	transitions: DiagramTransition[];
	dragDropState: DragDropState;
	reroutingTransition: ReroutingTransition;
	proposedTransition: ProposedTransition;
	onTransitionCreate?: (transition: DiagramTransition) => void;
	onTransitionPortChange?: (transition: DiagramTransition) => void;
	onTransitionProposalCancel: () => void;
	onTransitionReroutingCancel: () => void;
}

/**
 * Handles transition drag-drop logic for starting, moving, completing and closing:
 * * new transitions (internally called 'proposals')
 * * editing transition ports (internally called 'rerouting')
 */
export const useTransitionDragDrop = ({
	ports,
	transitions,
	dragDropState,
	reroutingTransition,
	proposedTransition,
	onTransitionCreate,
	onTransitionPortChange,
	onTransitionReroutingCancel,
	onTransitionProposalCancel,
}: Options): void => {
	const latestPorts = useLatest(ports);
	const latestTransitions = useLatest(transitions);
	const latestReroutingTransition = useLatest(reroutingTransition);
	const latestProposedTransition = useLatest(proposedTransition);

	useEffect(() => {
		if (dragDropState.isDragging) {
			return;
		}

		if (latestProposedTransition.current.stage === 'started') {
			// dragging ended without finishing creation of new transition

			onTransitionProposalCancel();
			return;
		}

		if (latestProposedTransition.current.stage === 'valid') {
			// dragging ended while finishing creation of new transition

			const transition = createTransitionFromProposed(
				latestProposedTransition.current,
				latestPorts.current,
			);

			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onTransitionCreate?.(transition);
			onTransitionProposalCancel();
			return;
		}

		if (latestReroutingTransition.current.stage === 'started') {
			// dragging ended without finishing change of ports of existing transition

			onTransitionReroutingCancel();
			return;
		}

		if (latestReroutingTransition.current.stage === 'valid') {
			// dragging ended while finishing change of ports of existing transition

			const updatedTransition = updateTransitionAfterReroute(
				latestReroutingTransition.current,
				latestPorts.current,
			);
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onTransitionPortChange?.(updatedTransition);
			onTransitionReroutingCancel();
		}
	}, [
		onTransitionProposalCancel,
		dragDropState,
		proposedTransition,
		latestReroutingTransition,
		latestTransitions,
		onTransitionCreate,
		onTransitionPortChange,
		latestProposedTransition,
		onTransitionReroutingCancel,
		latestPorts,
	]);
};
